import React from "react";
import { Plus } from "lucide-react";

// Iterative-style tree renderer to avoid recursive JSX (babel plugin edge case).
// Uses a helper `renderNode` that builds React elements via React.createElement.

function NodeCard({ role, users, isDirector, isManager, onAdd }) {
  return (
    <div
      className={`iz-card min-w-[240px] max-w-[280px] p-4 relative transition-all ${
        isDirector
          ? "border-[#d4af37] shadow-[0_0_30px_rgba(212,175,55,0.15)]"
          : isManager
          ? "border-[#d4af37]/40 hover:border-[#d4af37]"
          : "border-[#262626] hover:border-[#d4af37]/40"
      }`}
      data-testid={`hierarchy-node-${role.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className={`text-[9px] font-mono uppercase tracking-[0.25em] ${
            isDirector
              ? "text-[#d4af37]"
              : isManager
              ? "text-[#d4af37]/80"
              : "text-gray-500"
          }`}
        >
          {role.type}
        </span>
        <button
          onClick={() => onAdd(role)}
          className="w-6 h-6 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors flex items-center justify-center"
          title={`Add user to ${role.name}`}
          data-testid={`hierarchy-add-${role.id}`}
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>
      <p className="font-display text-lg text-white leading-tight">{role.name}</p>

      {users.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-[#1a1a1a] space-y-1.5">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-2 text-xs">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                  u.status === "active"
                    ? "bg-[#d4af37]/15 text-[#d4af37]"
                    : "bg-[#1a1a1a] text-gray-500"
                }`}
              >
                {u.name
                  .split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-200 truncate">{u.name}</p>
              </div>
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  u.status === "active" ? "bg-[#d4af37]" : "bg-gray-600"
                }`}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 pt-3 border-t border-[#1a1a1a] text-[10px] text-gray-600 uppercase font-mono tracking-widest">
          Vacant
        </p>
      )}
    </div>
  );
}

function renderNode(node, onAdd) {
  const { role, users, children } = node;
  const isDirector = role.type === "director";
  const isManager = role.type === "manager";

  const card = React.createElement(NodeCard, {
    role,
    users,
    isDirector,
    isManager,
    onAdd,
  });

  const kids = children.length > 0
    ? React.createElement(
        "div",
        { className: "flex flex-col items-center w-full", key: "kids" },
        React.createElement("div", { className: "w-px h-8 bg-[#262626]" }),
        React.createElement(
          "div",
          { className: "flex gap-6 items-start pt-0 relative" },
          children.length > 1 &&
            React.createElement("div", {
              className: "absolute top-0 left-6 right-6 h-px bg-[#262626]",
              key: "hline",
            }),
          children.map((c) =>
            React.createElement(
              "div",
              { key: c.role.id, className: "flex flex-col items-center" },
              React.createElement("div", { className: "w-px h-8 bg-[#262626]" }),
              renderNode(c, onAdd)
            )
          )
        )
      )
    : null;

  return React.createElement(
    "div",
    { key: role.id, className: "flex flex-col items-center relative" },
    card,
    kids
  );
}

export default function HierarchyTree({ tree, onAdd }) {
  if (!tree) return null;
  return renderNode(tree, onAdd);
}
